export enum CartEvents {
  ADD = "add_to_cart",
  REMOVE = "remove_from_cart",
}

export enum WishlistEvents {
  ADD = "add_to_wishlist",
  REMOVE = "remove_from_wishlist",
}

export class SolarDataLayer {
  static instance: SolarDataLayer;

  dataLayer: Array<object>;
  static currency: string = "EUR";
  static config: object = {
    item_id: "product_id",
    parent_product_id: "parent_product_id_i",
    item_group_id: "item_group_id",
    item_name: "name",
    item_variant_id: "item_variant_id",
    item_brand: "manufacturer",
    item_categories: "category_names",
    item_categories_id: "category_id",
    item_variant: "item_variant",
    price: "final_price_incl_tax",
    old_price: "price_incl_tax",
  };

  /**
   * Create new dataLayer element when there is none
   */
  constructor() {
    this.dataLayer = window.dataLayer || [];
  }

  /**
   * Product list used to store list items for observable implementation
   *
   * @protected
   */
  protected static productList: Array<object> = [];

  /**
   * listType used to store the list type for observable implementation
   *
   * @protected
   */
  protected static listType: string = "";

  /**
   * Default config for observable item list
   *
   * @protected
   */
  protected static observableConfig: object = {
    listSelector: "impressions-observable",
    itemsPerRowDesktop: 4,
    itemsPerRowMobile: 2,
  };

  /**
   *
   * @protected
   */
  protected static isMobile: boolean =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  /**
   *
   * @param config
   * @param currency
   */
  static init(config?: object, currency?: string) {
    if (SolarDataLayer.instance) {
      return SolarDataLayer.instance;
    }

    if (config) {
      SolarDataLayer.config = {
        ...SolarDataLayer.config,
        ...config,
      };
    }

    if (currency) {
      SolarDataLayer.currency = currency;
    }

    SolarDataLayer.instance = new SolarDataLayer();

    return SolarDataLayer.instance;
  }

  /**
   *
   * @param value
   */
  static dataLayerValue(value: any): any {
    return value || "";
  }

  /**
   * Reset dataLayer
   */
  static resetDataLayer(): void {
    window.dataLayer.push({ ecommerce: null });
  }

  /**
   *
   * @param value
   */
  static formatPrice(value: number | string): number {
    const price = typeof value === "string" ? parseFloat(value) : value;
    const multiplier = Math.pow(10, 2);
    return Math.round(price * multiplier) / multiplier;
  }

  /**
   *
   * @param categories
   * @param position
   */
  static resolveCategory(categories: Array<string>, position: number): string {
    return categories[position] || "";
  }

  /**
   *
   * @param oldPrice
   * @param price
   */
  static getSalePrice(oldPrice: number, price: number): number | string {
    return oldPrice !== price ? oldPrice - price : "";
  }

  /**
   *
   * @param product
   */
  static defaultProductInformation(product: object): object {
    const config = SolarDataLayer.config;

    const productData = {
      item_name: SolarDataLayer.dataLayerValue(product[config["item_name"]]),
      item_id: SolarDataLayer.dataLayerValue(product[config["item_id"]]),
      item_parent_id: SolarDataLayer.dataLayerValue(
        product[config["parent_product_id"]] || product["parent_product_id"]
      ),
      item_group_id: SolarDataLayer.dataLayerValue(
        product[config["item_group_id"]]
      ),
      item_brand: "HouthandelOnline",
      item_variant_id: SolarDataLayer.dataLayerValue(
        product[config["item_variant_id"]]
      ),
      item_variant: SolarDataLayer.dataLayerValue(
        product[config["item_variant"]]
      ),
      currency: SolarDataLayer.currency,
      price: SolarDataLayer.dataLayerValue(
        SolarDataLayer.formatPrice(product[config["price"]])
      ),
      discount: SolarDataLayer.getSalePrice(
        SolarDataLayer.formatPrice(product[config["old_price"]]),
        SolarDataLayer.formatPrice(product[config["price"]])
      ),
    };

    let categoryData = {};
    for (let i = 0; i < 5; i++) {
      categoryData[`item_category${i > 0 ? i + 1 : ""}`] =
        SolarDataLayer.resolveCategory(product["category_names"], i);
    }

    let categoryIDData = {};

    if (
      product &&
      product["category_id"] &&
      typeof product["category_id"] === "object"
    ) {
      let categoryIds = Object.values(product["category_id"]);

      if (Array.isArray(categoryIds)) {
        categoryIds.splice(0, 2); // Remove first 2 items

        for (let i = 0; i < 5; i++) {
          categoryIDData[`item_category_id${i > 0 ? i + 1 : ""}`] =
            categoryIds[i] || "";
        }
      }
    }

    return {
      ...productData,
      ...categoryData,
      ...categoryIDData,
    };
  }

  /**
   *
   * @param product
   */
  static quoteProductInformation(product: object): object {
    const config = SolarDataLayer.config;

    const productData = {
      item_name: SolarDataLayer.dataLayerValue(product[config["item_name"]]),
      item_id: SolarDataLayer.dataLayerValue(
        product["extension_attributes"]?.[config["item_variant_id"]] ||
          product["extension_attributes"]?.["item_variant_id"]
      ),
      item_parent_id: SolarDataLayer.dataLayerValue(
        product["extension_attributes"][config["parent_product_id"]] ||
          product["extension_attributes"]["parent_product_id"]
      ),
      item_group_id: SolarDataLayer.dataLayerValue(
        product[config["item_group_id"]]
      ),
      item_brand: "HouthandelOnline",
      item_variant_id: SolarDataLayer.dataLayerValue(
        product["extension_attributes"][config["item_variant_id"]]
      ),
      item_variant: SolarDataLayer.dataLayerValue(
        product["extension_attributes"][config["item_variant"]]
      ),
      currency: SolarDataLayer.currency,
      price: SolarDataLayer.dataLayerValue(
        SolarDataLayer.formatPrice(product["price_incl_tax"])
      ),
      discount: SolarDataLayer.getSalePrice(
        SolarDataLayer.formatPrice(
          product["extension_attributes"]["from_price"]
        ),
        SolarDataLayer.formatPrice(product["price_incl_tax"])
      ),
      quantity: SolarDataLayer.dataLayerValue(product["qty"]),
    };

    let categoryData = {};

    //Check if category data is present on the quote item
    const categoryItemsRaw =
      product["extension_attributes"][config["item_categories"]];
    if (categoryItemsRaw) {
      const categoryItems =
        typeof categoryItemsRaw === "object"
          ? categoryItemsRaw
          : JSON.parse(categoryItemsRaw);
      for (let i = 0; i < 5; i++) {
        categoryData[`item_category${i > 0 ? i + 1 : ""}`] =
          SolarDataLayer.resolveCategory(categoryItems, i);
      }
    }

    return {
      ...productData,
      ...categoryData,
    };
  }

  /**
   *
   * @param products
   * @param listType
   * @param config
   */
  addObservableViewItemsList(
    products: Array<object>,
    listType?: string,
    config: null | object = null
  ): void {
    if (config) {
      SolarDataLayer.observableConfig = {
        ...SolarDataLayer.observableConfig,
        ...config,
      };
    }

    SolarDataLayer.productList = products;
    SolarDataLayer.listType = listType;

    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0,
    };

    const observer = new IntersectionObserver(
      SolarDataLayer.addViewItemsListEntries,
      observerOptions
    );
    const rootElement = document.getElementById(
      SolarDataLayer.observableConfig["listSelector"] ||
        "impressions-observable"
    );
    const itemsPerRow = SolarDataLayer.isMobile
      ? SolarDataLayer.observableConfig["itemsPerRowMobile"]
      : SolarDataLayer.observableConfig["itemsPerRowDesktop"];

    if (rootElement) {
      const observeTargets = rootElement.querySelectorAll("[data-index]");
      for (let i = 0; i < observeTargets.length; i++) {
        if (i % itemsPerRow === 0) {
          observer.observe(observeTargets[i]);
        }
      }
    }
  }

  /**
   *
   * @param entries
   * @param observer
   */
  static addViewItemsListEntries(
    entries: IntersectionObserverEntry[],
    observer: IntersectionObserver
  ): void {
    const productsClone = SolarDataLayer.productList;
    const itemsPerRow = SolarDataLayer.isMobile
      ? SolarDataLayer.observableConfig["itemsPerRowMobile"]
      : SolarDataLayer.observableConfig["itemsPerRowDesktop"];

    entries.map((entry) => {
      if (entry.isIntersecting && entry.target.hasAttribute("data-index")) {
        const itemIndex = parseInt(entry.target.getAttribute("data-index"));
        SolarDataLayer.init().addViewItemsList(
          productsClone.slice(itemIndex, itemIndex + itemsPerRow),
          SolarDataLayer.listType,
          itemIndex
        );

        observer.unobserve(entry.target);
      }
    });
  }

  /**
   *
   * @param products
   * @param listType
   * @param index
   */
  addViewItemsList(
    products: Array<object>,
    listType?: string,
    index: number = 0
  ): void {
    if (
      !window.dataLayer.filter((e) => e["event"] === "view_item_list").length
    ) {
      SolarDataLayer.resetDataLayer();
    }

    let items: Array<object> = [];

    products.forEach((product: object) => {
      index++;

      const productData = {
        item_list_id: listType.replace(/ /g, "_").toLowerCase(),
        item_list_name: listType,
        index: index,
      };

      items.push(
        Object.assign(
          {},
          productData,
          SolarDataLayer.defaultProductInformation(product)
        )
      );
    });

    window.dataLayer.push({
      event: "view_item_list",
      ecommerce: {
        items: items,
      },
    });
  }

  /**
   *
   * @param product
   * @param index
   * @param listType
   */
  addSelectItem(
    product: object,
    index: number | string,
    listType?: string
  ): void {
    SolarDataLayer.resetDataLayer();

    let items: Array<object> = [];

    const productData = {
      item_list_id: listType.replace(/ /g, "_").toLowerCase(),
      item_list_name: listType,
      index: index,
    };

    items.push(
      Object.assign(
        {},
        productData,
        SolarDataLayer.defaultProductInformation(product)
      )
    );

    window.dataLayer.push({
      event: "select_item",
      ecommerce: {
        currency: SolarDataLayer.currency,
        items: items,
      },
    });
  }

  /**
   *
   * @param product
   */
  addViewItem(product: object): void {
    SolarDataLayer.resetDataLayer();

    let extraOptions = {};

    if (
      product["product_type"] === "bundle" &&
      Object.prototype.hasOwnProperty.call(product, "bundle_options")
    ) {
      let items: Array<object> = [];

      product["bundle_options"].forEach((bundleProduct: object) => {
        items.push(SolarDataLayer.defaultProductInformation(bundleProduct));
      });

      extraOptions = {
        productType: "bundle",
        bundleItems: items,
      };
    }

    window.dataLayer.push({
      event: "view_item",
      ecommerce: {
        currency: SolarDataLayer.currency,
        items: [
          {
            ...SolarDataLayer.defaultProductInformation(product),
            ...extraOptions,
          },
        ],
      },
    });
  }

  /**
   * Add or remove items from cart
   *
   * @param product
   * @param event
   * @param qty
   * @param cartValue
   */
  addAddToCart(
    product: object,
    event: CartEvents = CartEvents.ADD,
    qty: number = 1,
    cartValue: number = 0
  ): void {
    SolarDataLayer.resetDataLayer();

    let items: Array<object> = [];

    const productData = {
      quantity: qty,
    };

    if (event === CartEvents.ADD) {
      items.push(
        Object.assign(
          {},
          productData,
          SolarDataLayer.defaultProductInformation(product)
        )
      );
    } else {
      items.push(
        Object.assign(
          {},
          productData,
          SolarDataLayer.quoteProductInformation(product)
        )
      );
    }

    window.dataLayer.push({
      event: event,
      ecommerce: {
        currency: SolarDataLayer.currency,
        value: cartValue > 0 ? SolarDataLayer.formatPrice(cartValue) : null,
        items: items,
      },
    });
  }

  /**
   *
   * Add or remove items from wishlist
   *
   * @param product
   * @param event
   */
  addAddToWishlist(
    product: object,
    event: WishlistEvents = WishlistEvents.ADD
  ): void {
    SolarDataLayer.resetDataLayer();

    window.dataLayer.push({
      event: event,
      ecommerce: {
        items: SolarDataLayer.defaultProductInformation(product),
      },
    });
  }

  /**
   *
   * @param products
   * @param cartValue
   * @param couponCode
   */
  addViewCart(
    products: Array<object>,
    cartValue: number,
    couponCode: string = ""
  ) {
    SolarDataLayer.resetDataLayer();

    let items: Array<object> = [];

    products.forEach((product: object, index: number) => {
      const productData = {
        index: index,
        coupon: couponCode,
      };

      items.push(
        Object.assign(
          {},
          productData,
          SolarDataLayer.quoteProductInformation(product)
        )
      );
    });

    window.dataLayer.push({
      event: "view_cart",
      ecommerce: {
        currency: SolarDataLayer.currency,
        value: SolarDataLayer.formatPrice(cartValue),
        items: items,
      },
    });
  }

  /**
   *
   * @param products
   * @param cartValue
   * @param couponCode
   */
  addBeginCheckout(
    products: Array<object>,
    cartValue: number,
    couponCode: string = ""
  ) {
    SolarDataLayer.resetDataLayer();

    let items: Array<object> = [];

    products.forEach((product: object, index: number) => {
      const productData = {
        index: index,
        coupon: couponCode,
      };

      items.push(
        Object.assign(
          {},
          productData,
          SolarDataLayer.quoteProductInformation(product)
        )
      );
    });

    window.dataLayer.push({
      event: "begin_checkout",
      ecommerce: {
        currency: SolarDataLayer.currency,
        value: SolarDataLayer.formatPrice(cartValue),
        items: items,
      },
    });
  }

  /**
   *
   * @param products
   * @param cartValue
   * @param shippingTier
   * @param couponCode
   */
  addAddShippingInfo(
    products: Array<object>,
    cartValue: number,
    shippingTier: string = "",
    couponCode: string = ""
  ) {
    SolarDataLayer.resetDataLayer();

    let items: Array<object> = [];

    products.forEach((product: object, index: number) => {
      const productData = {
        index: index,
        coupon: couponCode,
      };

      items.push(
        Object.assign(
          {},
          productData,
          SolarDataLayer.quoteProductInformation(product)
        )
      );
    });

    window.dataLayer.push({
      event: "add_shipping_info",
      ecommerce: {
        currency: SolarDataLayer.currency,
        value: SolarDataLayer.formatPrice(cartValue),
        coupon: couponCode,
        shipping_tier: shippingTier,
        items: items,
      },
    });
  }

  /**
   *
   * @param products
   * @param cartValue
   * @param paymentType
   * @param couponCode
   */
  addAddPaymentInfo(
    products: Array<object>,
    cartValue: number,
    paymentType: string = "",
    couponCode: string = ""
  ) {
    SolarDataLayer.resetDataLayer();

    let items: Array<object> = [];

    products.forEach((product: object, index: number) => {
      const productData = {
        index: index,
        coupon: couponCode,
      };

      items.push(
        Object.assign(
          {},
          productData,
          SolarDataLayer.quoteProductInformation(product)
        )
      );
    });

    window.dataLayer.push({
      event: "add_payment_info",
      ecommerce: {
        currency: SolarDataLayer.currency,
        value: SolarDataLayer.formatPrice(cartValue),
        coupon: couponCode,
        payment_type: paymentType,
        items: items,
      },
    });
  }

  /**
   *
   * @param products
   * @param transactionId
   * @param affiliation
   * @param cartValue
   * @param tax
   * @param shipping
   * @param couponCode
   */
  addPurchase(
    products: Array<object>,
    transactionId: string | number,
    affiliation: string = "",
    cartValue: number,
    tax: number,
    shipping: number,
    couponCode: string = ""
  ) {
    SolarDataLayer.resetDataLayer();

    let items: Array<object> = [];

    products.forEach((product: object, index: number) => {
      const productData = {
        index: index,
        coupon: couponCode,
        affiliation: affiliation,
      };

      items.push(
        Object.assign(
          {},
          productData,
          SolarDataLayer.quoteProductInformation(product)
        )
      );
    });

    window.dataLayer.push({
      event: "purchase",
      ecommerce: {
        transaction_id: transactionId,
        affiliation: affiliation,
        value: SolarDataLayer.formatPrice(cartValue),
        tax: SolarDataLayer.formatPrice(tax),
        shipping: SolarDataLayer.formatPrice(shipping),
        currency: SolarDataLayer.currency,
        coupon: couponCode,
        items: items,
      },
    });
  }
}
